import type {Edge, Node, NodeHandle} from "@xyflow/react";
import {Position} from "@xyflow/react";
import {hierarchy, tree as d3Tree} from "d3-hierarchy";
import type {SupplierEntity} from "~/pages/supplier-360/types/Supplier360Types";
import type {
    ValueChainDirection,
    ValueChainTree,
    ValueChainTreeNode,
} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainLinkTypes";

export type FocusNodeData = {
    entity: SupplierEntity;
};

export type CounterpartyNodeData = {
    link: ValueChainTreeNode;
    direction: ValueChainDirection;
    /** Names from the focus down to the company this one was reached through. */
    path: string[];
    /** Faded: another supply type than the filter, kept only because a match hangs off it. */
    dimmed: boolean;
    /** The counterparty's own links, one tier further out. */
    childCount: number;
    collapsed: boolean;
};

/** The placeholder for a side with no links. */
export type StubNodeData = {
    label: string;
};

/** A tier's column heading — a label only; tiers toggle from the cards and the tier panel. */
export type LaneNodeData = {
    label: string;
    align: 'left' | 'right';
};

export type FocusNode = Node<FocusNodeData, 'focus'>;
export type CounterpartyNode = Node<CounterpartyNodeData, 'counterparty'>;
export type StubNode = Node<StubNodeData, 'stub'>;
export type LaneNode = Node<LaneNodeData, 'lane'>;
export type ValueChainNode = FocusNode | CounterpartyNode | StubNode | LaneNode;

export const SIDE_WIDTH = 260;
const MID_WIDTH = SIDE_WIDTH * 1.12;
const COLUMN_GAP = 80;
export const COUNTERPARTY_HEIGHT = 80;
const STUB_HEIGHT = 36;
const ROW_GAP = 12;
const FOCUS_HEIGHT = 96;
const LANE_HEIGHT = 28;
const LANE_GAP = 16;
export const FOCUS_ID = 'focus';

/**
 * Edge anchors declared up front (incoming left, outgoing right), as in the taxonomy builder: React
 * Flow then doesn't have to measure the handles in the DOM before it can draw the edges.
 */
const handles = (width: number, height: number): NodeHandle[] => [
    {type: 'target', position: Position.Left, x: 0, y: height / 2},
    {type: 'source', position: Position.Right, x: width, y: height / 2},
];

/** "indirect_support_service" → "Indirect support service". */
export function humanizeCode(code: string): string {
    const text = code.replace(/_/g, ' ').trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

/** "T2" → "Tier 2"; anything else is shown as-is. */
export function tierLabel(tier: string): string {
    const match = /^T(\d+)$/i.exec(tier.trim());
    if (match) return `Tier ${match[1]}`;
    return tier.trim() || 'Unknown tier';
}

export function counterpartyName(link: ValueChainTreeNode): string {
    return link.counterparty_name || 'Unresolved counterparty';
}

/** The focus's own id on one side: the root its tier 1 hangs off. */
export function sideRootId(direction: ValueChainDirection): string {
    return `link-${direction}`;
}

/** A group has no id of its own (§2.17): the counterparty, else its newest source row, identifies it. */
function linkRef(link: ValueChainTreeNode, index: number): string {
    return String(link.counterparty_entity ?? `src-${link.sources[0]?.id ?? `i${index}`}`);
}

/** The tree with stable, path-based ids — a company reached through two parents is two nodes. */
type Placed = {
    id: string;
    /** `null` for the side root, which is drawn as the focus. */
    link: ValueChainTreeNode | null;
    path: string[];
    children: Placed[];
};

function placeSide(direction: ValueChainDirection, roots: ValueChainTreeNode[], focusName: string): Placed {
    const place = (link: ValueChainTreeNode, index: number, parentId: string, path: string[]): Placed => {
        const id = `${parentId}__${linkRef(link, index)}`;
        return {id, link, path, children: link.children.map((child, i) => place(child, i, id, [...path, counterpartyName(link)]))};
    };
    const rootId = sideRootId(direction);
    return {id: rootId, link: null, path: [], children: roots.map((root, i) => place(root, i, rootId, [focusName]))};
}

/** Ids of every counterparty that has a tier behind it — collapsing them all leaves only tier 1. */
export function parentIds(tree: ValueChainTree): string[] {
    const ids: string[] = [];
    const visit = (node: Placed) => {
        if (node.link && node.children.length > 0) ids.push(node.id);
        node.children.forEach(visit);
    };
    // The focus name only feeds the cards' paths, not their ids.
    visit(placeSide('upstream', tree.upstream, ''));
    visit(placeSide('downstream', tree.downstream, ''));
    return ids;
}

/** Every counterparty in the tree, both directions, every tier. */
export function flattenTree(tree: ValueChainTree): ValueChainTreeNode[] {
    const all: ValueChainTreeNode[] = [];
    const visit = (link: ValueChainTreeNode) => {
        all.push(link);
        link.children.forEach(visit);
    };
    [...tree.upstream, ...tree.downstream].forEach(visit);
    return all;
}

/** Supply types in the chain, most common first. */
export function supplyTypeOptions(tree: ValueChainTree): Array<{code: string; count: number}> {
    const counts = new Map<string, number>();
    flattenTree(tree).forEach(link => counts.set(link.supply_type, (counts.get(link.supply_type) ?? 0) + 1));
    return [...counts.entries()]
        .map(([code, count]) => ({code, count}))
        .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

/**
 * Keeps only the counterparties of one supply type — plus, faded, any other-type company a match
 * was reached through, so the match stays connected to the focus. `null` keeps everything.
 */
function filterBySupplyType(roots: ValueChainTreeNode[], supplyType: string | null): ValueChainTreeNode[] {
    if (supplyType === null) return roots;
    return roots.flatMap(link => {
        const children = filterBySupplyType(link.children, supplyType);
        return link.supply_type === supplyType || children.length > 0 ? [{...link, children}] : [];
    });
}

export type ValueChainGraphOptions = {
    /** Counterparties whose children are hidden (the board starts with all of them). Tier 1 is always shown. */
    collapsed: ReadonlySet<string>;
    /** Collapse or expand several nodes at once (a card, a tier's lane header, the "hidden" stub). */
    onToggleTier: (parentIds: string[], collapse: boolean) => void;
    /** Supply-type filter; other types are left out (see {@link filterBySupplyType}). `null` = All. */
    supplyType: string | null;
};

/** One tier of one side, for the tier panel — the one place a whole tier toggles. */
export type TierControl = {
    direction: ValueChainDirection;
    tier: string;
    collapsed: boolean;
    onToggle: () => void;
};

const DIRECTION_LABEL: Record<ValueChainDirection, string> = {
    upstream: 'Upstream · their suppliers',
    downstream: 'Downstream · buyers',
};

const DIRECTION_SHORT: Record<ValueChainDirection, string> = {
    upstream: 'Upstream',
    downstream: 'Downstream',
};

/** Ids of the nodes at each depth of the full (uncollapsed) tree; depth 0 is the side root. */
function idsByDepth(root: Placed): string[][] {
    const byDepth: string[][] = [];
    const visit = (node: Placed, depth: number) => {
        (byDepth[depth] ??= []).push(node.id);
        node.children.forEach(child => visit(child, depth + 1));
    };
    visit(root, 0);
    return byDepth;
}

/**
 * The taxonomy builder's left-to-right tidy tree, mirrored around the focus: upstream grows to the
 * left, downstream to the right, and every counterparty sits next to the one it was reached through.
 * Clicking a card collapses or expands its own children; the tier panel (tier 2 on, see
 * {@link TierControl}) does it for a whole tier. Tier 1 is always shown.
 */
export function buildValueChainGraph(entity: SupplierEntity, tree: ValueChainTree, options: ValueChainGraphOptions) {
    const nodes: ValueChainNode[] = [];
    const edges: Edge[] = [];
    /** Placed at y = 0 for now; they move above the tallest column once that's known. */
    const lanes: LaneNode[] = [];
    const tierControls: TierControl[] = [];
    let minY = -FOCUS_HEIGHT / 2;
    let maxY = FOCUS_HEIGHT / 2;

    const columnX = (direction: ValueChainDirection, depth: number) => direction === 'upstream'
        ? -MID_WIDTH / 2 - COLUMN_GAP - SIDE_WIDTH - (depth - 1) * (SIDE_WIDTH + COLUMN_GAP)
        : MID_WIDTH / 2 + COLUMN_GAP + (depth - 1) * (SIDE_WIDTH + COLUMN_GAP);

    const addSide = (direction: ValueChainDirection) => {
        const root = placeSide(direction, filterBySupplyType(tree[direction], options.supplyType), entity.canonical_name);
        const align = direction === 'upstream' ? 'left' : 'right';

        if (root.children.length === 0) {
            const x = columnX(direction, 1);
            nodes.push({
                id: `stub-${direction}`,
                type: 'stub',
                position: {x, y: -STUB_HEIGHT / 2},
                width: SIDE_WIDTH,
                height: STUB_HEIGHT,
                handles: handles(SIDE_WIDTH, STUB_HEIGHT),
                data: {label: options.supplyType !== null
                    ? `No ${direction === 'upstream' ? 'suppliers' : 'buyers'} of this type`
                    : direction === 'upstream' ? 'No suppliers' : 'No buyers'},
            });
            lanes.push({
                id: `lane-${direction}`,
                type: 'lane',
                position: {x, y: 0},
                width: SIDE_WIDTH,
                height: LANE_HEIGHT,
                data: {label: DIRECTION_LABEL[direction], align},
            });
            return;
        }

        // d3 lays out top-down; swap the axes for a left-to-right tree centred on the focus row.
        const layout = d3Tree<Placed>().nodeSize([COUNTERPARTY_HEIGHT + ROW_GAP, SIDE_WIDTH + COLUMN_GAP])(
            // Tier 1 always shows: only counterparties (never the side root) can hide their children.
            hierarchy(root, node => node.link && options.collapsed.has(node.id) ? [] : node.children),
        );
        const visible = new Set(layout.descendants().map(d => d.data.id));

        const byDepth = idsByDepth(root);
        byDepth.slice(1).forEach((ids, index) => {
            const depth = index + 1;
            const tier = `T${depth}`;
            // Collapsed = nothing of this tier is on screen, whether its own parents or an earlier tier hid it.
            const collapsed = !ids.some(id => visible.has(id));
            // Tier 1 can't be collapsed. Collapsing a deeper tier hides its parents' children;
            // expanding it re-opens every tier between it and tier 1 too.
            const onToggle = depth === 1 ? undefined : () => options.onToggleTier(
                collapsed ? byDepth.slice(1, depth).flat() : byDepth[depth - 1],
                !collapsed,
            );
            if (onToggle) tierControls.push({direction, tier, collapsed, onToggle});
            // A heading only over a tier that's on screen; a hidden one opens from the tier panel.
            if (collapsed) return;
            lanes.push({
                id: `lane-${direction}-${tier}`,
                type: 'lane',
                position: {x: columnX(direction, depth), y: 0},
                width: SIDE_WIDTH,
                height: LANE_HEIGHT,
                data: {
                    label: `${depth === 1 ? `${DIRECTION_SHORT[direction]} · ` : ''}${tierLabel(tier)} · ${ids.length}`,
                    align,
                },
            });
        });

        layout.descendants().forEach(d => {
            const {link} = d.data;
            if (!link) return;
            const dimmed = options.supplyType !== null && link.supply_type !== options.supplyType;
            const y = d.x - COUNTERPARTY_HEIGHT / 2;
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y + COUNTERPARTY_HEIGHT);
            nodes.push({
                id: d.data.id,
                type: 'counterparty',
                position: {x: columnX(direction, d.depth), y},
                width: SIDE_WIDTH,
                height: COUNTERPARTY_HEIGHT,
                handles: handles(SIDE_WIDTH, COUNTERPARTY_HEIGHT),
                // Cards with children toggle them on click.
                style: d.data.children.length > 0 ? {pointerEvents: 'all', cursor: 'pointer'} : undefined,
                data: {
                    link,
                    direction,
                    path: d.data.path,
                    dimmed,
                    childCount: d.data.children.length,
                    collapsed: options.collapsed.has(d.data.id),
                },
            });
            const parent = d.parent && d.parent.data.link ? d.parent.data.id : FOCUS_ID;
            // Upstream flows inwards to the focus, downstream outwards from it.
            const [source, target] = direction === 'upstream' ? [d.data.id, parent] : [parent, d.data.id];
            // The default edge is a bezier curve, like the design's `svc-wires`.
            edges.push({id: `edge-${source}->${target}`, source, target, type: 'default', data: {dimmed}});
        });
    };

    addSide('upstream');
    addSide('downstream');
    nodes.push({
        id: FOCUS_ID,
        type: 'focus',
        position: {x: -MID_WIDTH / 2, y: -FOCUS_HEIGHT / 2},
        width: MID_WIDTH,
        height: FOCUS_HEIGHT,
        handles: handles(MID_WIDTH, FOCUS_HEIGHT),
        data: {entity},
    });
    const laneY = minY - LANE_GAP - LANE_HEIGHT;
    lanes.forEach(lane => nodes.push({...lane, position: {x: lane.position.x, y: laneY}}));

    return {nodes, edges, tierControls, height: maxY - laneY};
}
